import type { ThunkAction, Action } from '@reduxjs/toolkit';
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import storage from 'redux-persist/lib/storage';
import { persistReducer, persistStore } from 'redux-persist';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { authApi } from 'src/api/auth.api';
import { api } from '../api/api';

import { authReducer } from './slices/authReducer.slice';
import { vehiclesReducer } from './slices/vehiclesReducer.slice';
import { driversReducer } from './slices/driversReducer.slice';
import { anomaliesReducer } from './slices/anomaliesReducer.slice';
import { nominatimApi } from '../api/nominatim-api';
import { liveviewApi } from '../api/liveview-api';
import { googleMapsApi } from '../api/google-maps-api';
import { userReducer } from './slices/userReducer.slice';
import { userManagementReducer } from './slices/usermanagementReducer.slice';
import { devicesReducer } from './slices/devicesReducer.slice';
import { organizationReducer } from './slices/organizationReducer.slice';
import { allVehiclesReducer } from './slices/allVehiclesReducer.slice';
import { releaseVersionsReducer } from './slices/releaseVersionReducer.slice';
import { userProfileReducer } from './slices/userProfileReducer.slice';
import { groupsReducer } from './slices/groupsReducer.slice';
import { routesReducer } from './slices/routesReducer.slice';

const userPersistConfig = {
  key: 'user',
  storage,
  whitelist: ['username', 'profilePictureKey'],
};

const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  [nominatimApi.reducerPath]: nominatimApi.reducer,
  [googleMapsApi.reducerPath]: googleMapsApi.reducer,
  [liveviewApi.reducerPath]: liveviewApi.reducer,
  authReducer: authReducer.reducer,
  vehiclesReducer: vehiclesReducer.reducer,
  driversReducer: driversReducer.reducer,
  anomaliesReducer: anomaliesReducer.reducer,
  devicesReducer: devicesReducer.reducer,
  userReducer: persistReducer(userPersistConfig, userReducer.reducer),
  [authApi.reducerPath]: authApi.reducer,
  organizationReducer: organizationReducer.reducer,
  allVehiclesReducer: allVehiclesReducer.reducer,
  releaseVersionsReducer: releaseVersionsReducer.reducer,
  userManagementReducer: userManagementReducer.reducer,
  userProfileReducer: userProfileReducer.reducer,
  groupsReducer: groupsReducer.reducer,
  routesReducer: routesReducer.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      api.middleware,
      nominatimApi.middleware,
      googleMapsApi.middleware,
      liveviewApi.middleware,
      authApi.middleware,
    ),
});

setupListeners(store.dispatch);

// Create the persistor for persisting the store
export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
